<template>
  <section class="l-inputs inputs">
    <div class="half-spacer"></div>

    <div class="total--warning" style="text-align:left; margin-bottom: .5rem; font-size: .8rem" >
     Adjust values below and click submit ⬇
    </div>

    <h2>Technology Percentage for Electricity Generation in 2050</h2>

    <b-container id="tech-mix">
      <b-row v-for="(source, name) in allInputs" :key="name" no-gutters>
        <b-col cols="5">
          <label for="demo1">{{ name }}:</label>
        </b-col>
        <b-col cols="7">
          <input-percent
            :value="source"
            :name="name"
            :min="0"
            :max="100"
            :step="1"
            :inputtable="true"
            @change="techMixInputChange"
            ref="tmix"
            :sum="techMixSum"
            inline
            center
            controls
            rounded
          ></input-percent>
          <div class="number-input__perc">%</div>
        </b-col>
      </b-row>

      <b-row no-gutters>
        <b-col></b-col>
        <b-col cols="7">
          <div class="total">
            <div class="total__label">Total:</div>
            <span class="total__number total__number--true">
              <span id="total--val">{{ formatPercentage(total) }}</span
              >%
            </span>
          </div>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <div class="warning-holder">
            <div class="total--warning" id="tech-mix-warning" v-if="this.inputsSum == 100">
              Reduce other values before increasing %
            </div>
          </div>
        </b-col>
      </b-row>
      <!-- <b-button @click="testMe" block variant="warning">Test</b-button> -->
    </b-container>

    <h2>Percentage of Light Duty Vehicles Driven on:</h2>
    <b-container id="ldv">
      <b-row v-for="(source, name) in allLightDuty" :key="name" no-gutters>
        <b-col cols="5">
          <label for="demo1">{{ name }}:</label>
        </b-col>
        <b-col cols="7">
          <input-percent
            v-if="name === 'Electricity'"
            :value="source"
            :name="name"
            :min="0"
            :max="100"
            :step="1"
            :inputtable="true"
            @change="lightDutyInputChange"
            driven
            inline
            center
            controls
            rounded
          ></input-percent>
          <input-percent
            v-else
            :value="source"
            :name="name"
            :min="0"
            :max="100"
            :step="1"
            :inputtable="true"
            driven
            disabled
            inline
            center
            controls
            rounded
          ></input-percent>
          <div class="number-input__perc">%</div>
        </b-col>
      </b-row>
    </b-container>

    <div class="spacer"></div>

    <h2>Percentage of Household Heating from:</h2>

    <b-container id="household-heating">
      <b-row v-for="(source, name) in allHeatingInputs" :key="name" no-gutters>
        <b-col cols="5">
          <label for="demo1">{{ name }}:</label>
        </b-col>
        <b-col cols="7">
          <input-percent
            :value="source.value"
            :name="name"
            :min="0"
            :max="source.max"
            :step="1"
            :inputtable="true"
            @change="heatingInputChange"
            @launchModal="$bvModal.show('heating-other-max-warning')"
            :sum="householdSum"
            inline
            center
            controls
          ></input-percent>
          <div class="number-input__perc">%</div>
        </b-col>
      </b-row>

      <b-row>
        <b-col></b-col>
        <b-col cols="8">
          <div class="heating-total">
            <div class="total__label">Total:</div>
            <span class="total__number total__number--true">
              <span id="total--val">{{ formatPercentage(otherTotal) }}</span
              >%
            </span>
          </div>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <div class="warning-holder">
            <div class="total--warning" id="household-warning" v-if="this.householdSum == 100">
              Reduce other values before increasing %
            </div>
          </div>
        </b-col>
      </b-row>
    </b-container>

    <div class="half-spacer"></div>

    <b-button
      @click="fetchData"
      block
      variant="success"
      :disabled="inputsSum < 100 || this.householdSum < 100"
      id="submit"
      >Submit</b-button
    >

    <b-modal id="heating-other-max-warning" hide-footer>
      <template v-slot:modal-title>
        Warning:
      </template>
      <div class="d-block text-center">
        <h6>
          As a simplifying assumption, the tool does not allow users to increase “other” residential heating fuels
          (these include biomass as well as petroleum fuels such as fuel oil and propane) above the historical
          percentage that appears as the default for each region.
        </h6>
      </div>
      <b-button class="mt-3" block @click="$bvModal.hide('heating-other-max-warning')">OK</b-button>
    </b-modal>

    <b-modal id="input-warning-modal" :visible="inputModalVisible" hide-footer>
      <template v-slot:modal-title>
        Warning:
      </template>
      <div v-for="(source, name) in getInputWarningModal" :key="name" class="d-block text-center">
        <h6 v-if="name === 'Nuclear'">
          You selected {{ source.input }}% {{ name }} electricity, but based on the assumptions of this calculator (100%
          capacity factor; maximum installed capacity equal to that of the minimum hourly demand) and your inputs,
          nuclear electricity is limited to {{ source.actual.toFixed(0) }}% of the total. The program defaults to
          calculate natural gas electricity generation to fill in all electricity generation that cannot be achieved due
          to renewable resource limitations. See the figure indicating “Actual Technology Mix for Electricity
          Generation” for the percentages as solved.
        </h6>
        <h6>
          You selected {{ source.input }}% {{ name }} electricity, but there is only enough available resource to
          deliver {{ source.actual.toFixed(2) }}% {{ name }} electricity. The program defaults to calculate natural gas
          electricity generation to fill in all electricity generation that cannot be achieved due to renewable resource
          limitations. See the figure indicating “Actual Technology Mix for Electricity Generation” for the percentages
          as solved.
        </h6>
      </div>
      <b-button class="mt-3" block @click="$store.commit('setCloseInputWarningModal', false)">OK</b-button>
    </b-modal>
    <b-modal id="light-duty-warning-modal" :visible="lightDutyModalVisible" hide-footer>
      <template v-slot:modal-title>
        NOTE:
      </template>
      <div class="d-block text-center">
        <h6>
          While you can submit your input percentage of light duty vehicles (LDVs) operating on electricity, this
          calculator assumes a fixed hourly charging profile for electric light duty vehicles (LDVs). This assumed
          profile is unlikely to be a good approximation if more than 20% of LDV miles are driven on electricity. When
          more than 20% of LDVs miles are driven on electricity, more sophisticated modeling is required to better
          understand changes to charging times each hour of the day and each season.”
        </h6>
      </div>
      <b-button class="mt-3" block @click="$store.commit('setCloseLightDutyWarningModal', false)">OK</b-button>
    </b-modal>
  </section>
</template>

<script>
import JQuery from 'jquery';
import InputPercent from './InputPercent';
import { mapGetters, mapActions, mapState } from 'vuex';

let $ = JQuery;

export default {
  name: 'Inputs',
  props: ['inputData'],
  components: {
    'input-percent': InputPercent
  },

  data() {
    return {
      techMixSum: 100,
      techMixData: [],
      electricityData: null,
      heatingData: []
      // householdSum: 0
    };
  },
  watch: {},
  mounted() {},
  updated() {
    if (this.inputsSum >= 100) {
      // this.techMixSum = 100;
      $('#tech-mix .number-input__button.number-input__button--plus').attr('disabled', 'disabled');
    } else {
      $('#tech-mix .number-input__button.number-input__button--plus').removeAttr('disabled');
    }

    if (this.householdSum >= 100) {
      // this.techMixSum = 100;
      $('#household-heating .number-input__button.number-input__button--plus').attr('disabled', 'disabled');
    } else {
      $('#household-heating .number-input__button.number-input__button--plus').removeAttr('disabled');
    }
  },
  methods: {
    ...mapActions([
      'fetchInputs',
      'changeInput',
      'changeLDV',
      'changeOtherLDV',
      'changeHeating',
      'changeHeatingElectricity',
      'localStorageTest'
    ]),
    fetchData() {
      // this.$emit('fetch-data');
      this.fetchInputs();
      // this.localStorageTest();
    },
    otherHeatingMaxWarning(maxValue, attemptedValue) {
      this.$this.$bvModal.show('heating-other-max-warning');
    },
    animateProgress() {
      this.$root.$on('bv::modal::shown', () => {
        $('.ancl').animate(
          {
            width: '100%'
          },
          25000,
          function() {}
        );
      });
    },
    techMixInputChange(newValue, oldValue, name) {
      if (oldValue || oldValue === 0) {
        let diff = Number((oldValue - newValue).toFixed(0));
        if (this.inputsSum - diff > 100) {
          // diff = 0;
        }
        let obj = { diff: diff, name: name };
        this.changeInput(obj);
        if (this.techMixSum - diff <= 100) {
          this.techMixSum = this.techMixSum - diff;
        }
        if (this.inputsSum == 100) {
          $('#tech-mix .total__number').addClass('total__number--true');
        } else {
          $('#tech-mix .total__number').removeClass('total__number--true');
        }
      }
    },
    lightDutyInputChange(newValue, oldValue, name) {
      if (oldValue || oldValue === 0) {
        let diff = Number((oldValue - newValue).toFixed(0));
        let obj = { diff: diff };
        this.changeLDV(obj);
      }
    },
    lightDutyOtherInputChange(newValue, oldValue, name) {
      if (oldValue || oldValue === 0) {
        let diff = Number((oldValue - newValue).toFixed(0));
        let obj = { diff: diff, name: name };
      }
    },
    heatingInputChange(newValue, oldValue, name) {
      if (oldValue || oldValue === 0) {
        let diff = Number((oldValue - newValue).toFixed(0));
        let obj = { diff: diff, name: name };
        this.changeHeating(obj);
        if (this.householdSum - diff <= 100) {
          this.householdSum = this.householdSum - diff;
        }
        if (this.householdSum == 100) {
          $('#household-heating .total__number').addClass('total__number--true');
          $('#household-heating .number-input__button.number-input__button--plus').attr('disabled', 'disabled');
        } else {
          $('#household-heating .total__number').removeClass('total__number--true');
          $('#household-heating .number-input__button.number-input__button--plus').removeAttr('disabled');
        }
      }
    },
    formatPercentage(value) {
      let val = value < 100 ? value : 100;
      return val;
    },
    pulseHelpMsg() {
      let i = 0;
      $('#tech-mix-warning').stop();
      $('#tech-mix-warning').show();
      function pulsate() {
        if (i >= 3) {
          // $("#tech-mix-warning").fadeOut(2000);
          return;
        }
        $('#tech-mix-warning')
          .animate({ opacity: 0.6 }, 500, 'linear')
          .animate({ opacity: 1 }, 500, 'linear', pulsate);
        i++;
      }
      pulsate();
    }
  },
  computed: {
    ...mapGetters([
      'allInputs',
      'allHeatingInputs',
      'allLightDuty',
      'inputsSum',
      'householdSum',
      'getldv_e',
      'getOtherldv_e',
      'getInputWarningModal'
    ]),
    ...mapState(['heatingInputs', 'ldv_e']),
    inputModalVisible() {
      return this.$store.state.inputWarningModal;
    },
    lightDutyModalVisible() {
      return this.$store.state.lightDutyWarningModal;
    },
    region() {
      return this.$store.state.region;
    },
    total: function() {
      if (this.inputsSum >= 100) {
        $('#tech-mix .number-input__button.number-input__button--plus').attr('disabled', 'disabled');
        return '100';
      } else {
        return parseFloat(this.inputsSum).toFixed(0);
      }
    },
    otherTotal: function() {
      if (this.householdSum > 100) {
        $('#household-heating .number-input__button.number-input__button--plus').attr('disabled', 'disabled');
        return '100';
      } else {
        return parseFloat(this.householdSum).toFixed(0);
      }
    },
    percentageValue: function() {
      return this.inputData[1].value + '%';
    }
  }
};
</script>

<style lang="scss" scoped>
h2 {
  padding-right: 1rem;
  margin: 0 0 0.75rem 0;
}

.container {
  padding: 0;
  margin-bottom: 0;
}

.l-inputs {
  min-width: 290px;
  max-width: 290px;
  padding: 1rem 1.75rem;
  margin-left: 0px;

  label {
    margin-top: 6px;
  }
}

.inputs {
  color: white;
  @include gradient(#57585a, #231f20, 0);
}

.total__label {
  display: inline-block;
  // margin-left: 10px;
  padding-top: 6px;
}

.total__number {
  height: 30px;
  float: right;

  font-size: 1.7rem;
  color: #ed6011;
  font-weight: bold;
  text-align: right;
}

.total--val {
  color: #ed6011;
}

.total__number--true {
  color: #28a745;
}

.total {
  float: left;
  width: 100%;
  text-align: left;
}

.warning-holder {
  height: 24px;
}

.total--warning {
  width: 100%;

  text-align: right;
  font-size: 0.75rem;
  line-height: 1.5;
  color: #ffc10b;
}

#ldv, {
  >div:last-child{
    label{
      font-size: .8rem;
      line-height: 1;
    }
  }
}

#household-heating{

   >div:nth-child(3){
    label{
      font-size: .8rem;
      line-height: 1;
    }
  }
}
</style>
